/**
 * QuickLogModal - Full-screen modal for mobile activity logging
 * Triggered from the FAB in mobile bottom nav
 */

import { useState } from 'react';
import {
  Send,
  UserCheck,
  UserPlus,
  MessageCircle,
  Mail,
  MailOpen,
  Mic,
  Heart,
  Repeat2,
  CalendarCheck,
  Calendar,
  PhoneForwarded,
  PhoneCall,
  Phone,
  Trophy,
  Gift,
  FileText,
  Video,
  X,
  Loader2,
  Check,
} from 'lucide-react';
import { useActivityTypes, useLogActivity, useTodaySummary } from '@hooks/useActivities';
import { useUIStore } from '@stores/ui.store';
import { QuantityInputModal } from '@components/QuantityInputModal';
import type { ActivityType } from '@lib/types';

// Map icon names to components
const iconMap: Record<string, React.ReactNode> = {
  Send: <Send size={24} />,
  UserCheck: <UserCheck size={24} />,
  UserPlus: <UserPlus size={24} />,
  MessageCircle: <MessageCircle size={24} />,
  Mail: <Mail size={24} />,
  MailOpen: <MailOpen size={24} />,
  Mic: <Mic size={24} />,
  Heart: <Heart size={24} />,
  Repeat2: <Repeat2 size={24} />,
  CalendarCheck: <CalendarCheck size={24} />,
  Calendar: <Calendar size={24} />,
  PhoneForwarded: <PhoneForwarded size={24} />,
  PhoneCall: <PhoneCall size={24} />,
  Phone: <Phone size={24} />,
  Trophy: <Trophy size={24} />,
  Gift: <Gift size={24} />,
  FileText: <FileText size={24} />,
  Video: <Video size={24} />,
};

interface MobileActivityButtonProps {
  type: ActivityType;
  count: number;
  onTap: (type: ActivityType) => void;
  isLogging: boolean;
}

function MobileActivityButton({ type, count, onTap, isLogging }: MobileActivityButtonProps): JSX.Element {
  const progress = type.daily_target > 0 ? Math.min((count / type.daily_target) * 100, 100) : 0;
  const isComplete = type.daily_target > 0 && count >= type.daily_target;

  return (
    <button
      onClick={() => onTap(type)}
      disabled={isLogging}
      aria-label={`Log ${type.name}. Current: ${count}${type.daily_target > 0 ? ` of ${type.daily_target}` : ''}${isComplete ? '. Target complete' : ''}`}
      aria-busy={isLogging}
      role="listitem"
      className={`
        relative p-4 rounded-xl
        bg-ybh-bg-tertiary border
        transition-all duration-200 ease-out
        overflow-hidden min-h-[100px]
        active:scale-95
        ${isComplete
          ? 'border-ybh-success/40'
          : 'border-white/[0.04]'
        }
        ${isLogging ? 'opacity-50' : ''}
      `}
    >
      {/* Progress fill background */}
      <div
        className="absolute inset-0 bg-gradient-to-t from-ybh-orange/20 to-transparent opacity-60 transition-all duration-500"
        style={{ clipPath: `inset(${100 - progress}% 0 0 0)` }}
      />

      {/* Complete checkmark */}
      {isComplete && (
        <div className="absolute top-2 right-2">
          <div className="w-5 h-5 rounded-full bg-ybh-success flex items-center justify-center">
            <Check size={12} className="text-ybh-text-primary" />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 bg-ybh-orange/10 text-ybh-orange">
          {isLogging ? (
            <Loader2 size={24} className="animate-spin" />
          ) : (
            iconMap[type.icon] ?? <Send size={24} />
          )}
        </div>
        <span className="text-sm font-medium text-ybh-text-primary truncate w-full">{type.name}</span>
        <div className="flex items-center gap-1 mt-1">
          <span
            className={`font-display text-lg ${count > 0 ? 'text-ybh-orange' : 'text-ybh-text-primary'}`}
          >
            {count}
          </span>
          {type.daily_target > 0 && (
            <span className="text-xs text-ybh-text-muted">/{type.daily_target}</span>
          )}
        </div>
      </div>
    </button>
  );
}

export function QuickLogModal(): JSX.Element {
  const { quickLogOpen, closeQuickLog } = useUIStore();
  const { data: types, isLoading: typesLoading } = useActivityTypes();
  const { data: today } = useTodaySummary();
  const logMutation = useLogActivity();
  const [loggingId, setLoggingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<{
    points: number;
    basePoints: number;
    bonusPoints: number;
    name: string;
    quantity: number;
  } | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<ActivityType | null>(null);

  const handleActivityTap = (type: ActivityType): void => {
    setSelectedActivity(type);
  };

  const handleConfirmLog = async (quantity: number, linkedinMessageId?: string): Promise<void> => {
    if (!selectedActivity) return;

    setLoggingId(selectedActivity.id);
    setSelectedActivity(null);

    try {
      const result = await logMutation.mutateAsync({ activityTypeId: selectedActivity.id, quantity, linkedinMessageId });
      setSuccessMessage({
        points: result.totalPoints,
        basePoints: result.basePoints,
        bonusPoints: result.bonusPoints,
        name: result.activityName,
        quantity: result.quantity,
      });
      setTimeout(() => setSuccessMessage(null), 3000);
    } finally {
      setLoggingId(null);
    }
  };

  const handleCancelQuantity = (): void => {
    setSelectedActivity(null);
  };

  // Build count map from today's progress
  const countMap = new Map<string, number>();
  if (today?.progress) {
    for (const p of today.progress) {
      countMap.set(p.activityTypeId, p.count);
    }
  }

  if (!quickLogOpen) return <></>;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center md:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-log-title"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={closeQuickLog}
          aria-hidden="true"
        />

        {/* Modal — bottom sheet on mobile, centered dialog on desktop */}
        <div className="relative w-full md:w-auto md:max-w-[720px] bg-ybh-bg-secondary rounded-t-3xl md:rounded-3xl max-h-[85vh] overflow-hidden animate-slide-up border border-[var(--color-divider)]">
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-ybh-text-muted/30 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-4">
            <div>
              <h2 id="quick-log-title" className="font-display text-2xl text-ybh-text-primary tracking-wide">LOG ACTIVITY</h2>
              <p className="text-xs text-ybh-text-muted mt-1">Tap to enter quantity</p>
            </div>
            <button
              onClick={closeQuickLog}
              className="w-10 h-10 rounded-full bg-ybh-bg-tertiary flex items-center justify-center"
              aria-label="Close modal"
            >
              <X size={20} className="text-ybh-text-muted" />
            </button>
          </div>

          {/* Success toast */}
          {successMessage && (
            <div className="mx-5 mb-4 p-4 rounded-xl bg-ybh-success/20 border border-ybh-success/40 animate-scale-in">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-ybh-success/30 flex items-center justify-center">
                  <Trophy size={20} className="text-ybh-success" />
                </div>
                <div>
                  <p className="font-display text-xl text-ybh-success tracking-wide">
                    +{successMessage.points} POINTS
                  </p>
                  <p className="text-sm text-ybh-text-secondary">
                    {successMessage.quantity > 1 && `${successMessage.quantity}x `}
                    {successMessage.name}
                  </p>
                </div>
              </div>

            </div>
          )}

          {/* Activity grid */}
          <div className="px-5 pb-8 overflow-y-auto max-h-[60vh]" role="group" aria-label="Activity types">
            {typesLoading ? (
              <div className="grid grid-cols-3 gap-3" aria-busy="true">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="h-24 bg-ybh-bg-tertiary rounded-xl animate-pulse" aria-hidden="true" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3" role="list">
                {types?.map((type) => (
                  <MobileActivityButton
                    key={type.id}
                    type={type}
                    count={countMap.get(type.id) ?? 0}
                    onTap={handleActivityTap}
                    isLogging={loggingId === type.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quantity Input Modal */}
      <QuantityInputModal
        isOpen={!!selectedActivity}
        activityType={selectedActivity}
        onConfirm={handleConfirmLog}
        onCancel={handleCancelQuantity}
        isLoading={!!loggingId}
      />
    </>
  );
}
